import { useEffect, useRef } from 'react';
import { useLocation } from 'react-router-dom';

import { Routes } from '@/constants/routes';
import { getPath } from '@/utils/router';

/**
 * Neuer Stand beim naechsten Seitenwechsel (Entscheid Alex 27.10.2026,
 * docs/tasks/auto-reload-neuer-stand.md). Ersetzt „kein Auto-Reload" vom
 * 22.07. — das Banner bleibt als Hinweis.
 *
 * Anlass: nach Deploy #368 buchten zwei VPs aus alten Tabs, in denen die
 * Ergebnis-Wand noch fehlte. Ein Banner, das niemand klickt, heilt den Tab nie.
 *
 * NUR BEIM WECHSEL DES PFADS, nie sofort: wer die Seite verlaesst, laesst ihren
 * Eingabezustand ohnehin zurueck. Query und Hash zaehlen nicht (Reiter,
 * Filter). Der Buchungs-Wizard ist EINE Route, seine Schritte wechseln den Pfad
 * nicht — ein Reload trifft deshalb nie einen halben Wizard.
 *
 * 🔥 AUSNAHME BERATUNGSSTRECKE: sie ist eine FOLGE von Pfaden (Lebensplan,
 * Planer, Rechner) unter `/home/customers/customer/…`, und ihr Zustand liegt
 * bis auf die Kunden-ID nur im Speicher. Ein Reload zwischen zwei ihrer
 * Schritte waere ein Reload mitten im Kundengespraech. Deshalb: zwischen zwei
 * Strecken-Pfaden nie; beim Betreten und beim Verlassen der Strecke wie
 * ueberall. Damit das auch beim Verlassen greift, haengt der Hook in `RootView`
 * (NeuerStandWaechter) und nicht im Home-Layout — die Strecke liegt neben
 * diesem, nicht darin.
 *
 * 🔥 KEIN ENDLOS-RELOAD. Je Tab (`sessionStorage`) steht, fuer welchen
 * Serverstand schon neu geladen wurde. Weicht derselbe Stand nach dem Reload
 * immer noch ab (z. B. gecachtes `index.html`), bleibt es beim Banner; erst ein
 * NEUER Serverstand darf wieder neu laden. Ist `sessionStorage` nicht nutzbar,
 * gibt es keine Sperre — dann auch keinen automatischen Reload.
 */
export const NEUGELADEN_FUER_KEY = 'bw.neuerStand.neugeladenFuer';

const STRECKE_PRAEFIX = `/${getPath(Routes.Home, Routes.Customers, Routes.Customer)}`;

const inStrecke = (pfad: string): boolean =>
  pfad === STRECKE_PRAEFIX || pfad.startsWith(`${STRECKE_PRAEFIX}/`);

const sperreGesetzt = (serverBuild: string): boolean | null => {
  try {
    return window.sessionStorage.getItem(NEUGELADEN_FUER_KEY) === serverBuild;
  } catch {
    return null;
  }
};

const sperreSetzen = (serverBuild: string): boolean => {
  try {
    window.sessionStorage.setItem(NEUGELADEN_FUER_KEY, serverBuild);
    return window.sessionStorage.getItem(NEUGELADEN_FUER_KEY) === serverBuild;
  } catch {
    return false;
  }
};

const useNeuladenBeimSeitenwechsel = (serverBuild: string | null): void => {
  const { pathname } = useLocation();
  const vorherigerPfad = useRef(pathname);

  useEffect(() => {
    const warPfad = vorherigerPfad.current;
    vorherigerPfad.current = pathname;

    // Kein neuer Stand, oder nur der Stand hat sich geaendert und nicht der
    // Pfad: warten auf den naechsten Seitenwechsel.
    if (serverBuild === null || warPfad === pathname) {
      return;
    }
    // Schritt innerhalb der Beratungsstrecke: nie (siehe Kopfkommentar).
    if (inStrecke(warPfad) && inStrecke(pathname)) {
      return;
    }
    // `true` = fuer diesen Stand schon neu geladen, `null` = Sperre nicht
    // lesbar. Beides heisst: Banner statt Reload.
    if (sperreGesetzt(serverBuild) !== false) {
      return;
    }
    if (!sperreSetzen(serverBuild)) {
      return;
    }
    window.location.reload();
  }, [pathname, serverBuild]);
};

export default useNeuladenBeimSeitenwechsel;
